import { Doc } from '@dadohr/shared/domainTypes/document';
import {
  ConcreteExperienceMessage,
  IExperience,
  MessageStatus
} from '@dadohr/shared/domainTypes/experience';
import { isEmpty, partition } from 'lodash';

export type MessageStats = {
  total: number;
  done: number;
  error: number;
  upcoming: number;
  skipped: number;
  bounced: number;
};

// Concrete `messages[]` record types that represent a send for a given msgId.
// Per-cohort ("CC"/bundled) sends write ..._PER_COHORT_* records; this getter
// previously matched only 'EXPERIENCE_MESSAGE', so a correctly-sent per-cohort
// message found no record → 'UNKNOWN' → its row was dropped from the tab and
// uncounted. The ParticipantDialog (groupConcreteMessages, scope-only) and the
// ExperienceLog already treat all three as one message send; this aligns the
// timeline-item People tab with them. (The BE half — denorm-failed cohort
// members silently marked SUCCESS — is bead new-main-n034.)
export const MESSAGE_SEND_CONCRETE_TYPES: string[] = [
  'EXPERIENCE_MESSAGE',
  'EXPERIENCE_MESSAGE_PER_COHORT_AGGREGATED',
  'EXPERIENCE_MESSAGE_PER_COHORT_UNNESTED'
];

export const getMostRecentSuccessfulConcreteMessageOrAnyOther = (
  msgId: string,
  concreteMessages: ConcreteExperienceMessage[],
  messagesStatuses: IExperience['messagesStatuses']
) => {
  // `concreteMessages` is canonically an array, but a data drift stored a lone
  // message object directly on some instances (Twilio/SMS). Coerce at the
  // boundary — wrapping the lone object so its send record is still found —
  // rather than calling `.filter` on a non-array (which crashes the People tab /
  // instance-detail view). Mirrors the BE guard in @dadohr/shared messageStatus.ts.
  const raw = concreteMessages as unknown;
  const safeConcreteMessages: ConcreteExperienceMessage[] = Array.isArray(raw)
    ? (raw as ConcreteExperienceMessage[])
    : raw && typeof raw === 'object'
      ? [raw as ConcreteExperienceMessage]
      : [];
  const msgsForThisParticularOneInMostRecentFirstOrder = safeConcreteMessages
    .filter(
      (m) => MESSAGE_SEND_CONCRETE_TYPES.includes(m.type) && m.scope?.msgId === msgId
    )
    .reverse();

  const mostRecentlySuccessful = msgsForThisParticularOneInMostRecentFirstOrder.find(
    (m) => m.status === 'OK'
  );

  const mostRecentUpcomingMessage = Object.keys(messagesStatuses).map(
    (msgStatus) => {
      const d: IExperience['messagesStatuses'] = {};
      const isMsg = msgStatus === msgId;
      if (isMsg) {
        d[msgStatus] = messagesStatuses[msgStatus];
      }
      return d;
    }
  );
  const mostRecentlySuccessfulUpcomingMessage = mostRecentUpcomingMessage.find(
    (m) => !isEmpty(m) && m[msgId].status === 'UPCOMING'
  );
  return (
    mostRecentlySuccessful ||
    msgsForThisParticularOneInMostRecentFirstOrder[0] ||
    (mostRecentlySuccessfulUpcomingMessage &&
      mostRecentlySuccessfulUpcomingMessage[msgId]) ||
    null
  );
};

export const getFullMessageStatusIncludingMostRecentConcreteMessageStatus = (
  instance: Doc<IExperience>,
  msgId: string
): 'OK' | 'ERROR' | 'BOUNCED' | 'UPCOMING' | 'SKIPPED' | 'UNKNOWN' => {
  const msgStatus = instance.data.messagesStatuses[msgId];
  if (!msgStatus) {
    return 'UNKNOWN';
  }

  // It's possible that we have a past sending record of a message, but it's still upcoming:
  // This can happen when the message was manually moved to the future, thus making it upcoming again
  if (msgStatus.status === MessageStatus.UPCOMING) {
    return 'UPCOMING';
  }

  // A TRIGGERED message is a pending trigger-chain item that has not fired yet —
  // treat it as not-yet-sent (like UPCOMING) so its row is visible + counted,
  // instead of falling through to 'UNKNOWN' which silently dropped it.
  if (msgStatus.status === MessageStatus.TRIGGERED) {
    return 'UPCOMING';
  }

  if (msgStatus.status === MessageStatus.SKIPPED) {
    return 'SKIPPED';
  }

  const message = getMostRecentSuccessfulConcreteMessageOrAnyOther(
    msgId,
    instance.data.messages,
    instance.data.messagesStatuses
  );

  if (message && message.status === 'OK') {
    return 'OK';
  }

  if (message && message.status === 'ERROR') {
    return 'ERROR';
  }
  if (message && message.status === 'BOUNCED') {
    return 'BOUNCED';
  }

  return 'UNKNOWN';
};

export const getStatsForMessage = (
  instances: Doc<IExperience>[],
  msgId: string
): MessageStats => {
  const stats: MessageStats = {
    total: 0,
    done: 0,
    error: 0,
    upcoming: 0,
    skipped: 0,
    bounced: 0
  };
  instances.forEach((instance) => {
    const fullStatus = getFullMessageStatusIncludingMostRecentConcreteMessageStatus(
      instance,
      msgId
    );
    switch (fullStatus) {
      case 'OK':
        stats.done++;
        return;
      case 'ERROR':
        stats.error++;
        break;
      case 'BOUNCED':
        stats.bounced++;
        break;
      case 'UPCOMING':
        stats.upcoming++;
        break;
      case 'SKIPPED':
        stats.skipped++;
        break;
      case 'UNKNOWN':
        return;
      default:
        return;
    }
    stats.total++;
  });
  return stats;
};

export const getInstancesThatHaveThisMessage = (
  instances: Doc<IExperience>[],
  msgId: string
) => {
  const [instancesThatHaveThisMessage] = partition(instances, (instance) => {
    return !!instance.data.messagesStatuses[msgId]; // if this is empty -> then they don't receive the message. They are not part of an audience that gets this
  });

  return instancesThatHaveThisMessage; // To be updated if we need instances that are not part of the audience
};
